import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";

/* ======================================================
   PHASE DEFINITIONS
====================================================== */
export const LITE_PHASES = {
  LITE_ONLY: "lite-only",
  SOFT_REVEAL: "soft-reveal",
  ELIGIBLE_FOR_PRO: "eligible-for-pro",
};

const getPhaseFromDay = (day = 1) => {
  if (day >= 7) return LITE_PHASES.ELIGIBLE_FOR_PRO;
  if (day >= 5) return LITE_PHASES.SOFT_REVEAL;
  return LITE_PHASES.LITE_ONLY;
};

/* ======================================================
   STORAGE
====================================================== */
const getStorageKey = (user) => {
  // Logged-in user
  if (user?._id) {
    return `lite_journey_${user._id}`;
  }

  // Guest user
  return "lite_journey_guest";
};
const ENTRY_KEY = "lite_entry_decided";

/* ======================================================
   CANONICAL JOURNEY SHAPE
====================================================== */
const DEFAULT_JOURNEY = {
  completedDays: [],
  currentDay: 1,
  day1Summary: null,
  purpose: null,
  direction: null,
  phase: LITE_PHASES.LITE_ONLY,
};

/* ======================================================
   NORMALIZATION
====================================================== */
const normalizeJourney = (raw = {}) => {
  const completedDays = Array.isArray(raw.completedDays)
    ? [...new Set(raw.completedDays)]
        .filter((d) => Number.isInteger(d) && d >= 1 && d <= 7)
        .sort((a, b) => a - b)
    : [];

  const lastCompleted = completedDays.length
    ? completedDays[completedDays.length - 1]
    : 0;

  const currentDay = Math.min(lastCompleted + 1, 7);

  return {
    ...DEFAULT_JOURNEY,
    ...raw,
    completedDays,
    currentDay,
    phase: getPhaseFromDay(currentDay),
    day1Summary: raw.day1Summary ?? null,
    purpose: raw.purpose ?? null,
    direction: raw.direction ?? null,
  };
};

const LiteContext = createContext(null);

/* ======================================================
   PROVIDER
====================================================== */
export const LiteProvider = ({ children }) => {
  const { user } = useAuth();
  const STORAGE_KEY = getStorageKey(user);
  const [loading, setLoading] = useState(true);
  const [liteJourney, setLiteJourney] = useState(
    normalizeJourney(DEFAULT_JOURNEY)
  );

  // ✅ NEW: ENTRY STATE
  const [entryDecided, setEntryDecided] = useState(false);

  /* ======================================================
     LOAD JOURNEY (LOCAL FIRST — CRITICAL)
  ====================================================== */
  useEffect(() => {
    const loadJourney = async () => {
      setLoading(true);

      let localJourney = normalizeJourney(DEFAULT_JOURNEY);

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          localJourney = normalizeJourney(JSON.parse(raw));
        }
      } catch {
        console.warn("[LiteContext] Failed to parse local journey");
      }

      setLiteJourney(localJourney);

      // ✅ Restore entry decision
      const entryFlag = localStorage.getItem(ENTRY_KEY);
      if (entryFlag === "true") {
        setEntryDecided(true);
      }

      // Backend sync (NON-BLOCKING)
      if (user) {
        try {
          const res = await axios.get("/lite/journey");
          if (res.data?.completedDays) {
            const merged = normalizeJourney({
              ...localJourney,
              completedDays: res.data.completedDays,
            });

            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            setLiteJourney(merged);
          }
        } catch {
          // Silent fail (by design)
        }
      }

      setLoading(false);
    };

    loadJourney();
  }, [user]);

  /* ======================================================
     DAY 1 — SUMMARY
  ====================================================== */
  const setDay1Summary = (summary) => {
    if (!summary?.trim()) return;

    setLiteJourney((prev) => {
      const updated = normalizeJourney({
        ...prev,
        day1Summary: summary.trim(),
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  /* ======================================================
     PURPOSE
  ====================================================== */
  const updatePurpose = ({ statement, refinedByAI }) => {
    if (!statement?.trim()) return;

    setLiteJourney((prev) => {
      const updated = normalizeJourney({
        ...prev,
        purpose: {
          statement: statement.trim(),
          refinedByAI: !!refinedByAI,
          confirmedAt: Date.now(),
        },
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  /* ======================================================
     DIRECTION
  ====================================================== */
  const selectDirection = (direction) => {
    if (!direction?.title) return;

    setLiteJourney((prev) => {
      const updated = normalizeJourney({
        ...prev,
        direction: {
          title: direction.title,
          summary: direction.summary,
          angleTags: direction.angleTags || [],
          selectedAt: Date.now(),
        },
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  /* ======================================================
     DAY COMPLETION
  ====================================================== */
  const markDayComplete = () => {
    setLiteJourney((prev) => {
      const day = prev.currentDay;
      if (prev.completedDays.includes(day)) return prev;

      const updated = normalizeJourney({
        ...prev,
        completedDays: [...prev.completedDays, day],
        lastCompletedAt: Date.now(),
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      if (user) {
        axios
          .post("/lite/progress", {
            completedDays: updated.completedDays,
            currentDay: updated.currentDay,
          })
          .catch(() => {});
      }

      return updated;
    });
  };

  /* ======================================================
     RESET
  ====================================================== */
  const startFreshDay = () => {
      const fresh = normalizeJourney(DEFAULT_JOURNEY);

      localStorage.removeItem(STORAGE_KEY);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));

      setLiteJourney(fresh);

      // ✅ DO NOT touch entryDecided here
    };

        // ✅ REQUIRED: restore API used by existing JSX
      const decideEntry = () => {
        localStorage.setItem(ENTRY_KEY, "true");
        setEntryDecided(true);
      };

      const resetEntryDecision = () => {
        localStorage.removeItem(ENTRY_KEY);
        setEntryDecided(false);
      };
      
  return (
    <LiteContext.Provider
      value={{
        loading,
        liteJourney,

        currentDay: liteJourney.currentDay,
        completedDays: liteJourney.completedDays,
        phase: liteJourney.phase,

        // ✅ NEW
        entryDecided,
        setEntryDecided,

        decideEntry,
        resetEntryDecision,

        setDay1Summary,
        updatePurpose,
        selectDirection,
        markDayComplete,
        startFreshDay,
      }}
    >
      {children}
    </LiteContext.Provider>
  );
};

export const useLite = () => {
  const ctx = useContext(LiteContext);
  if (!ctx) throw new Error("useLite must be used within LiteProvider");
  return ctx;
};